import type dayjs from 'dayjs'
import {
  getAllWeldsOnComponent,
  getComponentRadius,
} from 'features/assetModeling'
import isNil from 'lodash/isNil'
import {
  type ForecastComponentConfig,
  type GeckoAssetModel,
  GeckoComponentModelType,
  type GeckoMeasurement,
  type GeckoTMinMethod,
} from 'types'

import {
  createMethodTminSource,
  type FindCustomCrForShellCourseFn,
  getFindCustomCrForShellCourseFn,
  getLossBinValueFunction,
  getLossPerYearBinValueFunction,
  getRcaBinValueFunction,
  getRlBinValueFunction,
  getTminBinValueFunction,
} from './calculateDerivedValues'
import type { CalculateBinValueFunction } from './calculateDerivedValuesHelpers'

export interface BinValueFunctions {
  lossForCoordsFn: CalculateBinValueFunction | undefined
  lossPerYearForCoordsFn: CalculateBinValueFunction | undefined
  tminForCoordsFn: CalculateBinValueFunction | undefined
  rcaForCoordsFn: CalculateBinValueFunction | undefined
  rlForCoordsFn: CalculateBinValueFunction | undefined
}

export function getBinValueFunctions({
  assetModel,
  componentId,
  binHeightFt,
  binWidthFt,
  forecastComponentConfig,
  inspectionDate,
  tminMethod,
  customCR = false,
}: {
  assetModel: GeckoAssetModel | undefined
  componentId: string | undefined
  binHeightFt: GeckoMeasurement | undefined
  binWidthFt: GeckoMeasurement | undefined
  forecastComponentConfig: ForecastComponentConfig | undefined
  inspectionDate: dayjs.Dayjs | undefined
  tminMethod: GeckoTMinMethod | undefined
  customCR?: boolean
}): BinValueFunctions {
  // the functions to return, these will be populated in the try/catch blocks below
  let lossForCoordsFn: CalculateBinValueFunction | undefined
  let lossPerYearForCoordsFn: CalculateBinValueFunction | undefined
  let tminForCoordsFn: CalculateBinValueFunction | undefined
  let rcaForCoordsFn: CalculateBinValueFunction | undefined
  let rlForCoordsFn: CalculateBinValueFunction | undefined

  const componentRam = assetModel?.data.components.get(componentId ?? '')

  if (
    isNil(assetModel) ||
    isNil(binHeightFt) ||
    isNil(binWidthFt) ||
    isNil(inspectionDate) ||
    isNil(componentId) ||
    isNil(componentRam)
  ) {
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  // Loss
  try {
    lossForCoordsFn = getLossBinValueFunction({
      assetModel,
      inspectionDate,
    })
  } catch (e) {
    console.warn('Could not get Loss bin value function', e)
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  let findCustomCrForShellCourseFn: FindCustomCrForShellCourseFn | undefined

  try {
    findCustomCrForShellCourseFn = !customCR
      ? undefined
      : getFindCustomCrForShellCourseFn(
          componentRam,
          forecastComponentConfig ?? {}
        )
  } catch (e) {
    console.warn('Could not get CR by Shell Course function', e)
  }

  // Loss Per Year
  try {
    lossPerYearForCoordsFn = getLossPerYearBinValueFunction({
      inspectionDate,
      assetModel,
      corrosionAugmentationRegions: [], // TODO: if simulation extension is enabled, pass in the simulation data,
      findCustomCrForShellCourseFn,
    })
  } catch (e) {
    console.warn('Could not get Loss Per Year bin value function', e)
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  // T_min
  if (isNil(tminMethod)) {
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  const componentDiameter = getComponentRadius(componentRam)?.scale(2)
  const tminSource = createMethodTminSource(tminMethod)

  try {
    tminForCoordsFn = getTminBinValueFunction({
      tminSource,
      weldLines: getAllWeldsOnComponent(componentRam),
      forecastComponentConfig: forecastComponentConfig ?? {},
      binHeight: binHeightFt,
      binWidth: binWidthFt,
      diameter: componentDiameter,
      componentType: componentRam.type,
      componentId,
      componentBounds: componentRam.componentBounds,
      assetModel,
    })
  } catch (e) {
    console.warn('Could not get T_min bin value function', e)
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  // RCA
  try {
    rcaForCoordsFn = getRcaBinValueFunction({
      binWidth: binWidthFt,
      binHeight: binHeightFt,
      tminSource,
      weldLines: getAllWeldsOnComponent(componentRam),
      shellCourses:
        componentRam.type === GeckoComponentModelType.VerticalTankShell
          ? componentRam.coursesArray
          : [],
      forecastComponentConfig: forecastComponentConfig ?? {},
      inspectionDate,
      assetModel,
      componentId,
      componentType: componentRam.type,
      repairsToSimInstall: [],
    })
  } catch (e) {
    console.warn('Could not get RCA bin value function', e)
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  // RL
  try {
    rlForCoordsFn = getRlBinValueFunction({
      assetModel,
      componentId,
      inspectionDate,
      binWidth: binWidthFt,
      binHeight: binHeightFt,
      tminSource,
      weldLines: getAllWeldsOnComponent(componentRam),
      shellCourses:
        componentRam.type === GeckoComponentModelType.VerticalTankShell
          ? componentRam.coursesArray
          : [],
      forecastComponentConfig: forecastComponentConfig ?? {},
      corrosionAugmentationRegions: [],
      repairsToSimInstall: [],
      findCustomCrForShellCourseFn,
      componentType: componentRam.type,
    })
  } catch (e) {
    console.warn('Could not get RL bin value function', e)
    return {
      lossForCoordsFn,
      lossPerYearForCoordsFn,
      tminForCoordsFn,
      rcaForCoordsFn,
      rlForCoordsFn,
    }
  }

  return {
    lossForCoordsFn,
    lossPerYearForCoordsFn,
    tminForCoordsFn,
    rcaForCoordsFn,
    rlForCoordsFn,
  }
}
